import React, { useEffect, useState, useRef, useCallback } from 'react';
import { galleryAPI, GalleryImage, getAssetUrl } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import Lightbox from './Lightbox';

interface CarouselGalleryProps {
  className?: string;
}

const CarouselGallery: React.FC<CarouselGalleryProps> = ({ className = '' }) => {
  const { t } = useI18n();
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const scrollPositionRef = useRef(0);
  const lastTimeRef = useRef<number>(0);
  const firstSetWidthRef = useRef<number>(0);

  // Fetch carousel images
  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const response = await galleryAPI.getByType('carousel');
        if (response.data?.images) {
          setImages(response.data.images);
        }
      } catch (err) {
        console.error('Error fetching carousel images:', err);
        setError(t.gallery?.loadError || 'Failed to load gallery');
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [t.gallery?.loadError]);

  // Calculate the width of the first set of images (for seamless looping)
  const calculateFirstSetWidth = useCallback(() => {
    if (!scrollRef.current || images.length === 0) return 0;

    const container = scrollRef.current;
    const children = container.children;
    let width = 0;

    // Sum up the widths of only the first set of images (not the duplicates)
    for (let i = 0; i < images.length && i < children.length; i++) {
      const child = children[i] as HTMLElement;
      width += child.offsetWidth;
    }

    return width;
  }, [images.length]);

  // Auto-scroll animation using transform for smoother GPU-accelerated movement
  const animate = useCallback(
    (timestamp: number) => {
      if (!scrollRef.current || images.length === 0) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      // Initialize lastTimeRef on first frame
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
      }

      // Calculate delta time for frame-rate independent animation
      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      if (!isPaused) {
        // Calculate the first set width if not yet calculated or recalculate periodically
        if (firstSetWidthRef.current === 0) {
          firstSetWidthRef.current = calculateFirstSetWidth();
        }

        const scrollWidth = firstSetWidthRef.current;

        // Only proceed if we have a valid width
        if (scrollWidth > 0) {
          // Use time-based movement for consistent speed across different refresh rates
          // 0.1 pixels per millisecond = 100 pixels per second (smooth and consistent)
          const pixelsPerMs = 0.1;
          scrollPositionRef.current += pixelsPerMs * deltaTime;

          // Reset position seamlessly when we've scrolled past the first set of images
          if (scrollPositionRef.current >= scrollWidth) {
            scrollPositionRef.current = scrollPositionRef.current - scrollWidth;
          }

          // Use transform instead of scrollLeft for GPU-accelerated smooth animation
          scrollRef.current.style.transform = `translateX(${-scrollPositionRef.current}px)`;
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    },
    [isPaused, images.length, calculateFirstSetWidth]
  );

  useEffect(() => {
    if (images.length > 0) {
      // Reset refs when images change
      lastTimeRef.current = 0;
      firstSetWidthRef.current = 0;
      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, images.length]);

  // Recalculate width when images finish loading
  const handleImageLoad = useCallback(() => {
    // Reset the cached width so it gets recalculated on next animation frame
    firstSetWidthRef.current = 0;
  }, []);

  const handleImageClick = (index: number) => {
    // Calculate the actual image index (since images are duplicated for infinite scroll)
    const actualIndex = index % images.length;
    setLightboxIndex(actualIndex);
    setLightboxOpen(true);
    setIsPaused(true);
  };

  const handleLightboxClose = () => {
    setLightboxOpen(false);
    setIsPaused(false);
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    if (!lightboxOpen) {
      setIsPaused(false);
    }
  };

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="flex h-64 items-center justify-center md:h-80">
          <div className="text-gray-500">{t.common.loading}</div>
        </div>
      </div>
    );
  }

  if (error || images.length === 0) {
    return null; // Don't show anything if there's an error or no images
  }

  // Duplicate images for infinite scroll effect
  const duplicatedImages = [...images, ...images];

  return (
    <div className={`overflow-hidden ${className}`}>
      <div
        ref={scrollRef}
        className="flex whitespace-nowrap"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          willChange: 'transform',
          transform: 'translateX(0px)',
        }}
      >
        {duplicatedImages.map((image, index) => (
          <div
            key={`${image.id}-${index}`}
            className="flex-shrink-0 cursor-pointer transition-transform duration-300 hover:z-10 hover:scale-105"
            onClick={() => handleImageClick(index)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleImageClick(index);
              }
            }}
            aria-label={t.gallery?.viewImage || 'View image'}
          >
            <img
              src={getAssetUrl(image.thumbnail_url || image.image_url)}
              alt=""
              className="h-64 w-auto object-cover md:h-80"
              loading="lazy"
              onLoad={handleImageLoad}
            />
          </div>
        ))}
      </div>

      <Lightbox
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={handleLightboxClose}
        onNavigate={setLightboxIndex}
      />
    </div>
  );
};

export default CarouselGallery;
